<template>
    <DialogFinalizeDocument
        v-if="display.finalizeDialogType !== ''"
        :validate="handleValidate"
        :canUpdateDate="canUpdateDate"
        :forceComplianceAlert="!isDocumentCompliant"
        :fetchMetadata="fetchFinalizeMetadata"
        :isDepositInvoice="isDeposit"
        @close="display.finalizeDialogType = ''"
    />
    <slot
        :display="display"
        :tabs="tabs"
        :hasDefaultCode="hasDefaultCode"
        :rowtype="rowtype"
        :loading="loading"
        :statistics="statistics"
        :doctype="doctype"
        :identity="identity"
        :splitter="splitter"
        :pdfLink="pdfLink"
        :displayPopupEditRowAccountingCode="displayPopupEditRowAccountingCode"
    />
</template>
<script>
import Store from 'stores/overview/document/store';
import { createNamespacedHelpers } from 'vuex';

import Feature from 'Services/Feature';
import EmailMixins from 'mixins/EmailMixins';
import DialogFinalizeDocument from 'vcomponents/common/document/DialogFinalizeDocument.vue';
const { mapState, mapActions, mapMutations } = createNamespacedHelpers('documentOverview');
const { mapActions: popupMapActions } = createNamespacedHelpers('popupPanel');
export default {
    components: {
        DialogFinalizeDocument,
    },
    mixins: [EmailMixins],
    provide() {
        return {
            isDocumentCompliant: this.isDocumentCompliant,
        };
    },
    inheritAttrs: false,
    props: {
        docnumber: {
            type: String,
            required: true,
        },
        doclabel: {
            type: String,
            required: true,
        },
        docid: {
            type: Number,
            required: true,
        },
        doctype: {
            type: String,
            required: true,
        },
        step: {
            type: String,
            required: true,
        },
        docNumberFormat: {
            type: Object,
            required: true,
        },
        unitNumberFormat: {
            type: Object,
            required: true,
        },
        rateCategoryId: {
            type: Number,
            required: true,
        },
        thirdId: {
            type: Number,
            required: true,
        },
        layoutId: {
            type: Number,
            required: true,
        },
        langId: {
            type: Number,
            required: true,
        },
        dunningState: {
            type: String,
            required: false,
            default: 'disabled',
        },
        deadlines: {
            type: Object,
            required: false,
            default: () => ({}),
        },
        canEditDocument: {
            type: Boolean,
            required: true,
        },
        canBaseEditDocument: {
            type: Boolean,
            required: true,
        },
        hasExpensePlugin: {
            type: Boolean,
            required: true,
        },
        hasTimeTrackingPlugin: {
            type: Boolean,
            required: true,
        },
        pdfGenerated: {
            type: Boolean,
            required: false,
            default: true,
        },
        initialPdfLink: {
            type: String,
            required: true,
        },
        fileid: {
            type: String,
            default: '',
        },
        initDate: {
            type: String,
            required: true,
        },
        lastValidationDate: {
            type: String,
            required: true,
        },
        canUpdateDate: {
            type: Boolean,
            required: true,
        },
        isDocumentCompliant: {
            type: Boolean,
            required: true,
        },
        isDeposit: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            rowid: false,
            rowtype: '',
            hasDefaultCode: false,
            display: {
                bonusForm: false,
                finalizeDialogType: '',
                cancelByCreditNote: false,
                changeByCreditNote: false,
                editRowAccountingCode: false,
            },
            tabs: {
                margin: false,
                dunning: false,
            },
            loading: !this.pdfGenerated,
            pdfLink: this.initialPdfLink,
            sendEmailEvent: false,
            generatePDFEvent: false,
            emailPanelOpened: false,
        };
    },
    computed: {
        ...mapState(['statistics', 'fileId', 'docFile']),
        identity() {
            return {
                type: this.doclabel,
                id: this.docid,
            };
        },
        isVisibleDocument() {
            return Feature.isEnabled('email-engine') ? 'Y' : 'N';
        },
        splitter() {
            return new RegExp('\\?_f=' + this.doctype + 'Overview&id=(\\d+)');
        },
    },
    mounted() {
        DocOverview.init();
        //for async invoice only, hide the loader when pdf is available
        if (['invoice', 'creditnote'].includes(this.doctype)) {
            if (!this.loading) {
                this.doAction();
            } else {
                //handle event when document pdf is generated
                EventBus.on('document:generated', (event) => {
                    if (parseInt(event.id) === parseInt(this.docid) && this.loading) {
                        this.pdfReadyCallback(event.publicLink, event.fileid);
                    }
                });

                //try to fetch PDF link in first, in the case of mercure notifcation has been sent, but JS was not ready
                this.pingPdf();

                //Fallback if mercure is down, try to fetch PDF every 3 seconds
                const intervalID = setInterval(() => {
                    if (this.loading) {
                        this.pingPdf();
                    } else {
                        clearInterval(intervalID);
                    }
                }, 3000);
            }
        }
        const marginBlock = document.getElementById('overview-indicators');
        if (marginBlock !== null) {
            this.getStatistics();
        }
    },
    async created() {
        this.$store.registerModule('documentOverview', Store);

        this.$store.commit('documentOverview/init', {
            docid: this.docid,
            doctype: this.doctype,
            doclabel: this.doclabel,
            step: this.step,
            rateCategoryId: this.rateCategoryId,
            docNumberFormat: this.docNumberFormat,
            unitNumberFormat: this.unitNumberFormat,
            thirdId: this.thirdId,
            layoutId: this.layoutId,
            langId: this.langId,
            dunningState: this.dunningState,
            deadlines: this.deadlines,
            canEditDocument: this.canEditDocument,
            canBaseEditDocument: this.canBaseEditDocument,
            hasExpensePlugin: this.hasExpensePlugin,
            hasTimeTrackingPlugin: this.hasTimeTrackingPlugin,
            docnumber: this.docnumber,
            fileId: this.fileid,
        });

        EventBus.on('sendMandateInvite', (data) => {
            this.checkEmailProvider({
                template: {
                    type: data.template,
                },
                linkedid: data.docId,
                type: data.docType,
                relatedid: data.docId,
                relatedtype: data.docType,
            });
        });

        EventBus.on('email:sendFinalInvoice', () => {
            this.sendEmailEvent = true;
            this.checkAndTriggerEmailPanelWithAttachment();
        });

        EventBus.on('email:sendDocument', async ({ type = 'document' }) => {
            await this.$store.dispatch('documentOverview/getDocFile');
            this.checkEmailProvider(
                {
                    template: {
                        type,
                        contact: {
                            id: this.docid,
                            type: this.doctype,
                        },
                        related: [
                            {
                                id: this.docid,
                                type: this.doctype,
                            },
                        ],
                    },
                    relatedtype: this.doctype,
                    relatedid: this.docid,
                    doclabel: this.doclabel,
                },
                { checkPrefill: true },
            );
        });

        if (!this.loading) {
            this.$store.dispatch('documentOverview/getDocFile');
        }

        EventBus.on('document:validate', this.openValidationPopup);
        EventBus.on('docBonusForm:close', () => {
            this.display.bonusForm = false;
        });
        EventBus.on('docEditRowAccountingCode:close', () => {
            this.display.editRowAccountingCode = false;
        });
        EventBus.on('docCancelByCreditNote:close', () => {
            this.display.cancelByCreditNote = false;
        });
        EventBus.on('docChangeByCreditNote:close', () => {
            this.display.changeByCreditNote = false;
        });
    },
    unmounted() {
        EventBus.off('document:generated');
        EventBus.off('sendMandateInvite');
        EventBus.off('email:sendDocument');
        EventBus.off('email:sendFinalInvoice');
        EventBus.off('document:validate');
        EventBus.off('docBonusForm:close');
        EventBus.off('docEditRowAccountingCode:close');
        EventBus.off('docCancelByCreditNote:close');
        EventBus.off('docChangeByCreditNote:close');
    },

    methods: {
        ...popupMapActions(['open']),
        async checkAndTriggerEmailPanelWithAttachment() {
            if (this.sendEmailEvent && this.generatePDFEvent && !this.emailPanelOpened) {
                this.emailPanelOpened = true;

                await this.$store.dispatch('documentOverview/getDocFile');

                this.checkEmailProvider(
                    {
                        template: {
                            type: 'document',
                            contact: {
                                id: this.docid,
                                type: this.doctype,
                            },
                            related: [
                                {
                                    id: this.docid,
                                    type: this.doctype,
                                },
                            ],
                        },
                        relatedtype: this.doctype,
                        relatedid: this.docid,
                        doclabel: this.doclabel,
                    },
                    { checkPrefill: true },
                );
            }
        },
        pdfReadyCallback(pdfLink, newFileId) {
            this.loading = false;
            this.pdfLink = pdfLink;
            this.$store.commit('documentOverview/setFileId', newFileId);
            this.$store.dispatch('documentOverview/getDocFile');
            this.generatePDFEvent = true;
            this.checkAndTriggerEmailPanelWithAttachment();
            this.doAction();
        },
        pingPdf() {
            SellsyJS.WebServiceCaller.get(
                `/rest/documents/${this.doctype}/${this.docid}/pdf`,
                {},
                (response) => {
                    if (response.pdfGenerated && this.loading) {
                        this.pdfReadyCallback(response.path, response.fileid);
                    }
                },
            );
        },
        doAction() {
            DocOverview.doAction(Persistent.get('afterRecording'));

            const urlParams = new URLSearchParams(location.search);
            if (urlParams.has('afterRecording')) {
                DocOverview.doAction(urlParams.get('afterRecording'), urlParams.get('id'));
            }
        },
        displayPopupEditRowAccountingCode(rowid, hasDefaultCode = false, rowtype = '') {
            this.selectRow(rowid);
            this.display.editRowAccountingCode = true;
            this.hasDefaultCode = hasDefaultCode;
            this.rowtype = rowtype;
        },
        openValidationPopup(type) {
            this.display.finalizeDialogType = type !== undefined ? type : 'validation';
        },
        fetchFinalizeMetadata() {
            return Promise.resolve({
                date: this.initDate,
                lastValidationDate: this.lastValidationDate || null,
                paymentsToLink: [],
            });
        },
        async handleValidate(date) {
            try {
                await this.validate({ date });
                if (this.display.finalizeDialogType !== 'validation') {
                    const current = window.location.search;
                    const regex = new RegExp('([?;&])afterRecording[^&;]*[;&]?');
                    const query = current.replace(regex, '$1').replace(/&$/, '');
                    window.location.search =
                        (query.length > 2 ? query + '&' : '?') +
                        (this.display.finalizeDialogType
                            ? 'afterRecording=' + this.display.finalizeDialogType
                            : '');
                } else {
                    location.reload();
                }
            } catch {
                throw new Error(this.$t.__('documents.DocOverview.error_date'));
            }
        },
        ...mapActions(['getStatistics', 'validate']),
        ...mapMutations(['selectRow']),
    },
};
</script>
